import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SupabaseLoginGate } from '../SupabaseLoginGate';
import { callEdgeFunction } from '../supabase.client';
import { DataTable } from '../components/DataTable';
import {
  loadSqlHistory,
  pushSqlHistory,
  clearSqlHistory,
  type SqlHistoryEntry,
} from './sql-history';
import type { ColumnDef } from '@tanstack/react-table';

type Env = 'dev' | 'staging' | 'production';

interface QueryResp {
  rows: Record<string, unknown>[];
  columns: string[];
  count: number;
  totalCount: number;
  truncated: boolean;
  durationMs: number;
}

export default function SqlQueryPage() {
  const [schema, setSchema] = useState<Env>('dev');
  const [sql, setSql] = useState('');
  const [running, setRunning] = useState(false);
  const [resp, setResp] = useState<QueryResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<SqlHistoryEntry[]>([]);

  useEffect(() => {
    setHistory(loadSqlHistory());
  }, []);

  const run = useCallback(async () => {
    if (!sql.trim() || running) return;
    setRunning(true);
    setError(null);
    setResp(null);
    try {
      const r = await callEdgeFunction<QueryResp>('query', { schema, sql });
      setResp(r);
      pushSqlHistory({
        schema,
        sql,
        durationMs: r.durationMs,
        rowCount: r.count,
        ts: Date.now(),
      });
      setHistory(loadSqlHistory());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }, [schema, sql, running]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void run();
    }
  };

  const copyJson = () => {
    if (resp) navigator.clipboard.writeText(JSON.stringify(resp.rows, null, 2));
  };
  const copyCsv = () => {
    if (!resp || resp.rows.length === 0) return;
    const cols = resp.columns;
    const esc = (v: unknown) => {
      const s =
        v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      cols.join(','),
      ...resp.rows.map((r) => cols.map((c) => esc(r[c])).join(',')),
    ].join('\n');
    navigator.clipboard.writeText(csv);
  };

  const cols: ColumnDef<Record<string, unknown>>[] = (resp?.columns ?? []).map((c) => ({
    accessorKey: c,
    header: c,
    cell: ({ getValue }) => {
      const v = getValue();
      if (v === null || v === undefined) return <span className="text-muted-foreground">NULL</span>;
      return typeof v === 'object' ? (
        <code className="text-xs">{JSON.stringify(v)}</code>
      ) : (
        String(v)
      );
    },
  }));

  return (
    <SupabaseLoginGate>
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <Select value={schema} onValueChange={(v) => setSchema(v as Env)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dev">dev</SelectItem>
              <SelectItem value="staging">staging</SelectItem>
              <SelectItem value="production">production</SelectItem>
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                History ({history.length})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[480px]">
              {history.length === 0 ? (
                <DropdownMenuItem disabled>(empty)</DropdownMenuItem>
              ) : (
                <>
                  {history.map((h) => (
                    <DropdownMenuItem
                      key={`${h.ts}-${h.schema}-${h.sql}`}
                      onClick={() => {
                        setSchema(h.schema);
                        setSql(h.sql);
                      }}
                    >
                      <span className="truncate font-mono text-xs">
                        [{h.schema}] {h.sql.slice(0, 60)}
                        {h.sql.length > 60 ? '…' : ''}
                      </span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      clearSqlHistory();
                      setHistory([]);
                    }}
                  >
                    Clear history
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Textarea
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="SELECT id, name FROM stage WHERE world_id = 1 LIMIT 50;"
          className="min-h-[200px] font-mono text-sm"
        />

        <div className="flex gap-2">
          <Button onClick={run} disabled={running || !sql.trim()}>
            {running ? 'Running…' : 'Run (⌘+Enter)'}
          </Button>
          <Button variant="outline" onClick={() => setSql('')} disabled={running}>
            Clear
          </Button>
          <Button variant="outline" onClick={copyCsv} disabled={!resp || resp.rows.length === 0}>
            Copy CSV
          </Button>
          <Button variant="outline" onClick={copyJson} disabled={!resp || resp.rows.length === 0}>
            Copy JSON
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription className="font-mono text-xs whitespace-pre-wrap">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {resp && (
          <>
            <div className="text-sm text-muted-foreground">
              ✓ {resp.count} rows in {resp.durationMs}ms
              {resp.truncated && (
                <span className="text-amber-600 dark:text-amber-400">
                  {' '}
                  · truncated from {resp.totalCount}
                </span>
              )}
            </div>
            <DataTable columns={cols} data={resp.rows} />
          </>
        )}
      </div>
    </SupabaseLoginGate>
  );
}
